import { pickT } from "@repo/shared";
import type { QuizOption, QuizQuestion } from "@repo/shared/types";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { T } from "./quiz-question-dialog.i18n";

type QuizQuestionDialogProps = {
  open: boolean;
  /** Null when adding; the question being edited otherwise. */
  question: QuizQuestion | null;
  language: string;
  onClose: () => void;
  onSave: (question: QuizQuestion) => void;
};

const blankOption = (): QuizOption => ({ id: crypto.randomUUID(), label: "", score: 0 });

/** QL-253 — the fixed "None + Category 1-10" slots; never custom-named. */
const CATEGORY_SLOTS = Array.from({ length: 10 }, (_, i) => i + 1);

/**
 * One question and its point-scored options. Plain strings, no `LocalizedInput`
 * — that component is where the AI-translate affordance comes from, and QL-13
 * is explicitly single-language.
 */
export function QuizQuestionDialog({
  open,
  question,
  language,
  onClose,
  onSave,
}: QuizQuestionDialogProps) {
  const t = pickT(T, language);
  const [label, setLabel] = useState("");
  const [options, setOptions] = useState<QuizOption[]>([]);
  const [category, setCategory] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(question?.label ?? "");
    setOptions(
      question?.options?.length
        ? question.options.map((o) => ({ ...o }))
        : [blankOption(), blankOption()],
    );
    setCategory(question?.category ?? null);
  }, [open, question]);

  const canSave =
    label.trim() !== "" && options.length >= 2 && options.every((o) => o.label.trim() !== "");

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      id: question?.id ?? crypto.randomUUID(),
      label: label.trim(),
      options: options.map((o) => ({ ...o, label: o.label.trim() })),
      category,
    });
  };

  const updateOption = (id: string, patch: Partial<QuizOption>) =>
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{question ? t.editQuestion : t.addQuestion}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quiz-question-category">{t.category}</Label>
            <Select
              value={category == null ? "none" : String(category)}
              onValueChange={(v) => setCategory(v === "none" ? null : Number(v))}
            >
              <SelectTrigger id="quiz-question-category" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t.categoryNone}</SelectItem>
                {CATEGORY_SLOTS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {`${t.category} ${n}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t.categoryHint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quiz-question-label">
              {t.questionLabel} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="quiz-question-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t.questionPlaceholder}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                {t.options} <span className="text-destructive">*</span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOptions((prev) => [...prev, blankOption()])}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t.addOption}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t.optionsHelp}</p>

            <div className="space-y-2">
              {options.map((option) => (
                <div key={option.id} className="flex items-center gap-2">
                  <Input
                    value={option.label}
                    onChange={(e) => updateOption(option.id, { label: e.target.value })}
                    placeholder={t.optionPlaceholder}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={option.score}
                    onChange={(e) =>
                      updateOption(option.id, { score: Number(e.target.value) || 0 })
                    }
                    aria-label={t.score}
                    className="w-24"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    tooltip={t.delete}
                    // Two options is the floor: a question with one answer asks nothing.
                    disabled={options.length <= 2}
                    onClick={() => setOptions((prev) => prev.filter((o) => o.id !== option.id))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {question ? t.save : t.addQuestion}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
